import React, { useState, useEffect } from "react";
import { MdOutlineStar } from "react-icons/md";
import axiosClient from "../../axios-client"; // Make sure axiosClient is properly set up

const AllReports = () => {
  const [savedStates, setSavedStates] = useState({});
  const [reportsData, setReportsData] = useState([]); // Default reports
  const userID = localStorage.getItem("userID");

  // Fetch reports and saved states from the server
  useEffect(() => {
    const fetchSavedReports = async () => {
      try {
        // Fetch saved reports for the user
        const response = await axiosClient.get(`/users/saved/report/${userID}`);

        // Map the response to set reportsData with the correct data format
        const fetchedReports = response.data.savedReports.map((report) => ({
          id: report._id,
          reportTitle: report.reportTitle,
          category: report.category,
          pdfUrl: report.pdfUrl,
          viewUrl: report.viewUrl,
        }));

        // Set the reports data
        setReportsData(fetchedReports);

        // Initialize savedStates where each report ID is mapped to a boolean indicating if it is saved
        const savedReportIds = fetchedReports.map((report) => report.id);
        setSavedStates(
          savedReportIds.reduce((acc, reportId) => {
            acc[reportId] = true;
            return acc;
          }, {})
        );
      } catch (error) {
        console.error("Error fetching saved reports:", error);
      }
    };

    fetchSavedReports();
  }, [userID]); // Re-fetch if the userID changes (although this is unlikely to change in practice)

  const toggleSave = (reportId) => {
    setSavedStates((prev) => ({
      ...prev,
      [reportId]: !prev[reportId], // Toggle the saved state of the specific report
    }));

    // Optionally, update the server about the change
    axiosClient.put(`/users/save/report/${userID}`, {
      reportIds: Object.keys(savedStates).filter((key) => savedStates[key]), // Update saved reports
    });
  };

  const handleViewReport = (viewUrl) => {
    window.open(viewUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadReport = async (pdfUrl, title) => {
    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading report:", error);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 pb-16 sm:pb-24 min-h-screen">
      <h2 className="font-inter text-2xl sm:text-3xl lg:text-4xl font-bold font4">
        Saved Reports
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-6">
        {reportsData.map((report) => (
          <div
            key={report._id}
            className="border rounded-2xl p-3 sm:p-4 box-saved hover:shadow-lg transition-shadow duration-300"
          >
            <div className="flex justify-between items-start gap-2">
              <div className="text-left flex-1">
                <h3 className="text-sm sm:text-base font-semibold font-poppins mb-1 line-clamp-2">
                  {report.reportTitle || "No title available"}
                </h3>
                <p className="text-xs sm:text-sm font9 font-dmSans text-gray-600">
                  {report.category}
                </p>
              </div>
              <button
                onClick={() => toggleSave(report.id)}
                className="transition-colors duration-200 flex-shrink-0"
              >
                <MdOutlineStar
                  className={`w-6 h-6 sm:w-8 sm:h-7 ${
                    savedStates[report.id] ? "text-pink-500" : "text-gray-400"
                  }`}
                />
              </button>
            </div>
            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2 sm:gap-4">
              <button
                onClick={() => handleViewReport(report.viewUrl)}
                className="text-xs sm:text-sm font2 font-dmSans bg-lightPink px-3 sm:px-4 py-2 rounded-md hover:bg-pink-200 flex items-center justify-center sm:justify-start gap-1 w-full sm:w-auto"
              >
                View Report
              </button>
              <button
                onClick={() =>
                  handleDownloadReport(report.pdfUrl, report.reportTitle)
                }
                className="text-xs sm:text-sm font2 font-dmSans font-medium text-white bg-pink px-3 sm:px-4 py-2 rounded-md flex items-center justify-center sm:justify-start gap-2 w-full sm:w-auto"
              >
                Download Report
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllReports;
